import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, ShoppingCart, RotateCw, Check, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useCart } from '../context/CartContext';
import { toast } from '../hooks/use-toast';

const CustomDesignPage = () => {
  const { addToCart } = useCart();
  const [selectedPhone, setSelectedPhone] = useState('iphone-16-pro');
  const [selectedPattern, setSelectedPattern] = useState(0);
  const [selectedColor, setSelectedColor] = useState('classic');
  const [isRotating, setIsRotating] = useState(false);

  const phones = [
    { id: 'iphone-16-pro', name: 'iPhone 16 Pro', image: 'https://images.unsplash.com/photo-1592750475338-74b7b21085ab?w=400&h=800&fit=crop' },
    { id: 'iphone-16', name: 'iPhone 16', image: 'https://images.unsplash.com/photo-1510557880182-3d4d3cba35a5?w=400&h=800&fit=crop' },
    { id: 'iphone-15-pro', name: 'iPhone 15 Pro', image: 'https://images.unsplash.com/photo-1591337676887-a217a6970a8a?w=400&h=800&fit=crop' },
    { id: 'samsung-s25', name: 'Samsung S25', image: 'https://images.unsplash.com/photo-1610945415295-d9bbf067e59c?w=400&h=800&fit=crop' },
    { id: 'samsung-s24', name: 'Samsung S24', image: 'https://images.unsplash.com/photo-1565849904461-04a58ad377e0?w=400&h=800&fit=crop' },
  ];

  const kilimPatterns = [
    {
      id: 'geometric-diamond',
      name: 'Geometric Diamond',
      preview: 'https://customer-assets.emergentagent.com/job_ce00ca01-0951-4822-8b72-1668a488d246/artifacts/uqsrp4eo_IMG_1070.jpeg',
      css: `repeating-linear-gradient(45deg, 
        #78350f 0px, #78350f 10px, 
        #dc2626 10px, #dc2626 20px, 
        #f59e0b 20px, #f59e0b 30px,
        #78350f 30px, #78350f 40px)`,
      price: 49.99
    },
    {
      id: 'traditional-stripe',
      name: 'Traditional Stripe',
      preview: 'https://customer-assets.emergentagent.com/job_ce00ca01-0951-4822-8b72-1668a488d246/artifacts/93wmkx6j_IMG_1071.jpeg',
      css: `repeating-linear-gradient(0deg, 
        #78350f 0px, #78350f 8px, 
        #fbbf24 8px, #fbbf24 16px, 
        #dc2626 16px, #dc2626 24px,
        #ea580c 24px, #ea580c 32px)`,
      price: 44.99
    },
    {
      id: 'bohemian-wave',
      name: 'Bohemian Wave',
      preview: null,
      css: `repeating-linear-gradient(135deg, 
        #b45309 0px, #b45309 15px, 
        #fef3c7 15px, #fef3c7 20px, 
        #dc2626 20px, #dc2626 35px,
        #fef3c7 35px, #fef3c7 40px)`,
      price: 54.99
    },
    {
      id: 'moroccan-tiles',
      name: 'Moroccan Tiles',
      preview: null,
      css: `repeating-conic-gradient(from 0deg at 50% 50%, 
        #78350f 0deg, #78350f 90deg, 
        #dc2626 90deg, #dc2626 180deg,
        #f59e0b 180deg, #f59e0b 270deg,
        #ea580c 270deg, #ea580c 360deg)`,
      price: 59.99
    },
    {
      id: 'aztec-sun',
      name: 'Aztec Sun',
      preview: null,
      css: `radial-gradient(circle at center, 
        #fbbf24 0%, #fbbf24 10%, 
        #dc2626 10%, #dc2626 20%,
        #78350f 20%, #78350f 30%,
        #ea580c 30%, #ea580c 40%,
        #fbbf24 40%, #fbbf24 50%,
        transparent 50%)`,
      price: 64.99
    },
    {
      id: 'nomad-zigzag',
      name: 'Nomad Zigzag',
      preview: null,
      css: `repeating-linear-gradient(90deg,
        transparent 0px, transparent 20px,
        #78350f 20px, #78350f 22px),
        repeating-linear-gradient(45deg,
        #dc2626 0px, #dc2626 10px,
        #fbbf24 10px, #fbbf24 20px)`,
      price: 49.99
    }
  ];

  const colorSchemes = [
    { id: 'classic', name: 'Classic', colors: ['#78350f', '#dc2626', '#f59e0b'] },
    { id: 'ocean', name: 'Ocean', colors: ['#164e63', '#0891b2', '#67e8f9'] },
    { id: 'forest', name: 'Forest', colors: ['#14532d', '#22c55e', '#86efac'] },
    { id: 'sunset', name: 'Sunset', colors: ['#7c2d12', '#ea580c', '#fcd34d'] },
    { id: 'royal', name: 'Royal', colors: ['#581c87', '#9333ea', '#e879f9'] },
  ];

  const handleAddToCart = async () => {
    // In a real implementation, this would add a custom product
    toast({
      title: "Custom design saved!",
      description: `Your ${kilimPatterns[selectedPattern].name} design has been added to cart.`,
    });
  };

  const rotatePattern = () => {
    setIsRotating(true);
    setTimeout(() => setIsRotating(false), 500);
  };

  return (
    <div className="min-h-screen bg-stone-50" data-testid="custom-design-page">
      {/* Header */}
      <div className="bg-stone-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `
              repeating-linear-gradient(45deg, transparent, transparent 40px, #fbbf24 40px, #fbbf24 42px),
              repeating-linear-gradient(-45deg, transparent, transparent 40px, #dc2626 40px, #dc2626 42px)
            `
          }}
        ></div>
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-12 relative z-10">
          <div className="flex items-center gap-3 mb-4">
            <Sparkles className="h-5 w-5 text-amber-400" />
            <span className="text-amber-400 text-sm tracking-[0.2em] uppercase">Design Studio</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-black text-white mb-2">Custom Design</h1>
          <p className="text-stone-400">Create your unique kilim-inspired phone case</p>
        </div>
        <div className="h-2"
          style={{
            backgroundImage: `repeating-linear-gradient(90deg, 
              #78350f 0px, #78350f 30px, 
              #dc2626 30px, #dc2626 60px, 
              #f59e0b 60px, #f59e0b 90px)`
          }}
        ></div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Preview Section */}
          <div className="sticky top-8">
            <div className="bg-white shadow-xl p-8 border-t-4 border-amber-600" data-testid="phone-preview">
              <h2 className="text-2xl font-black text-stone-900 mb-6">Live Preview</h2>
              
              {/* Phone Mockup */}
              <div className="relative flex justify-center items-center py-8">
                <div className="relative w-64 h-[500px]">
                  {/* Phone Frame */}
                  <div className="absolute inset-0 bg-stone-900 rounded-[3rem] shadow-2xl"></div>
                  
                  {/* Phone Screen */}
                  <div className="absolute inset-2 bg-stone-800 rounded-[2.5rem] overflow-hidden">
                    {/* Case Pattern Overlay */}
                    <div 
                      className={`absolute inset-0 transition-transform duration-500 ${isRotating ? 'rotate-180' : ''}`}
                      style={{ 
                        background: kilimPatterns[selectedPattern].css,
                        opacity: 0.9
                      }}
                      data-testid="pattern-preview"
                    ></div>
                    
                    {/* Phone Screen Content */}
                    <div className="absolute inset-4 bg-stone-900/80 backdrop-blur-sm rounded-[2rem] flex flex-col items-center justify-center">
                      <div className="text-center text-white">
                        <p className="text-4xl font-black mb-2">Amlaria</p>
                        <p className="text-sm text-stone-400">{phones.find(p => p.id === selectedPhone)?.name}</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Camera Notch */}
                  <div className="absolute top-4 left-1/2 -translate-x-1/2 w-24 h-6 bg-stone-900 rounded-full"></div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="flex justify-center gap-4 mt-6">
                <Button 
                  variant="outline" 
                  onClick={rotatePattern}
                  className="rounded-none border-stone-300"
                  data-testid="rotate-btn"
                >
                  <RotateCw className={`h-4 w-4 mr-2 ${isRotating ? 'animate-spin' : ''}`} />
                  Rotate
                </Button>
              </div>

              {/* Selected Info */}
              <div className="mt-8 p-4 bg-amber-50 border-l-4 border-amber-600">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm text-stone-600">Your Custom Design</p>
                    <p className="font-bold text-lg text-stone-900">{kilimPatterns[selectedPattern].name}</p>
                  </div>
                  <p className="text-3xl font-black text-stone-900">${kilimPatterns[selectedPattern].price}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Customization Options */}
          <div className="space-y-8">
            {/* Phone Selection */}
            <div className="bg-white shadow-lg p-6 border-l-4 border-amber-600" data-testid="phone-selection">
              <h3 className="text-xl font-black text-stone-900 mb-4">1. Select Your Phone</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {phones.map((phone) => (
                  <button
                    key={phone.id}
                    onClick={() => setSelectedPhone(phone.id)}
                    className={`p-4 border-2 transition-all ${
                      selectedPhone === phone.id 
                        ? 'border-amber-600 bg-amber-50' 
                        : 'border-stone-200 hover:border-amber-400'
                    }`}
                    data-testid={`phone-${phone.id}`}
                  >
                    <p className="font-semibold text-stone-900 text-sm">{phone.name}</p>
                    {selectedPhone === phone.id && (
                      <Check className="h-4 w-4 text-amber-600 mx-auto mt-2" />
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Pattern Selection */}
            <div className="bg-white shadow-lg p-6 border-l-4 border-red-600" data-testid="pattern-selection">
              <h3 className="text-xl font-black text-stone-900 mb-4">2. Choose Kilim Pattern</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {kilimPatterns.map((pattern, index) => (
                  <button
                    key={pattern.id}
                    onClick={() => setSelectedPattern(index)}
                    className={`relative aspect-square overflow-hidden border-2 transition-all ${
                      selectedPattern === index 
                        ? 'border-amber-600 ring-2 ring-amber-400' 
                        : 'border-stone-200 hover:border-amber-400'
                    }`}
                    data-testid={`pattern-${pattern.id}`}
                  >
                    <div 
                      className="absolute inset-0"
                      style={{ background: pattern.css }}
                    ></div>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                    <div className="absolute bottom-2 left-2 right-2">
                      <p className="text-white text-xs font-semibold">{pattern.name}</p>
                      <p className="text-amber-300 text-xs">${pattern.price}</p>
                    </div>
                    {selectedPattern === index && (
                      <div className="absolute top-2 right-2 w-6 h-6 bg-amber-600 flex items-center justify-center">
                        <Check className="h-4 w-4 text-white" />
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Color Scheme Selection */}
            <div className="bg-white shadow-lg p-6 border-l-4 border-orange-600" data-testid="color-selection">
              <h3 className="text-xl font-black text-stone-900 mb-4">3. Color Scheme</h3>
              <div className="flex flex-wrap gap-3">
                {colorSchemes.map((scheme) => (
                  <button
                    key={scheme.id}
                    onClick={() => setSelectedColor(scheme.id)}
                    className={`flex items-center gap-2 px-4 py-3 border-2 transition-all ${
                      selectedColor === scheme.id 
                        ? 'border-amber-600 bg-amber-50' 
                        : 'border-stone-200 hover:border-amber-400'
                    }`}
                    data-testid={`color-${scheme.id}`}
                  >
                    <div className="flex gap-1">
                      {scheme.colors.map((color, i) => (
                        <div 
                          key={i} 
                          className="w-4 h-4" 
                          style={{ backgroundColor: color }}
                        ></div>
                      ))}
                    </div>
                    <span className="font-medium text-sm text-stone-700">{scheme.name}</span>
                    {selectedColor === scheme.id && (
                      <Check className="h-4 w-4 text-amber-600" />
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Summary & Add to Cart */}
            <div className="bg-stone-900 p-6 text-white" data-testid="design-summary">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h3 className="text-xl font-black mb-2">Your Custom Case</h3>
                  <ul className="space-y-1 text-sm text-stone-400">
                    <li>Phone: {phones.find(p => p.id === selectedPhone)?.name}</li>
                    <li>Pattern: {kilimPatterns[selectedPattern].name}</li>
                    <li>Color: {colorSchemes.find(c => c.id === selectedColor)?.name}</li>
                  </ul>
                </div>
                <div className="text-right">
                  <p className="text-sm text-stone-400">Total Price</p>
                  <p className="text-4xl font-black text-amber-400">${kilimPatterns[selectedPattern].price}</p>
                </div>
              </div>
              
              <Button 
                onClick={handleAddToCart}
                className="w-full bg-amber-600 hover:bg-amber-700 text-white py-6 text-lg rounded-none"
                size="lg"
                data-testid="add-custom-to-cart"
              >
                <ShoppingCart className="h-5 w-5 mr-2" />
                Add Custom Design to Cart
              </Button>

              <p className="text-center text-stone-500 text-sm mt-4">
                Production time: 3-5 business days
              </p>
            </div>

            {/* Info Box */}
            <div className="bg-amber-50 border border-amber-200 p-4">
              <h4 className="font-bold text-stone-900 mb-2">About Our Custom Cases</h4>
              <ul className="space-y-2 text-sm text-stone-700">
                <li className="flex items-start gap-2">
                  <Check className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  Premium quality materials with lifetime warranty
                </li>
                <li className="flex items-start gap-2">
                  <Check className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  Patterns inspired by traditional kilim rugs
                </li>
                <li className="flex items-start gap-2">
                  <Check className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  Each case is uniquely printed to order
                </li>
                <li className="flex items-start gap-2">
                  <Check className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  Supports wireless charging
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomDesignPage;
